<template>
    <div class="tools-page">
        <page-header title="Tools" subtitle="Admin" />

        <div class="tools-grid">
            <!-- Duplicate Profiles Tool -->
            <div class="tool-card" v-if="!$store.getters.isEnterpriseAdminOrg && isAdminOrSuper">
                <div class="tool-card-header">
                    <div class="tool-icon">
                        <v-icon color="white">mdi-account-multiple-outline</v-icon>
                    </div>
                    <h3 class="tool-title">Find Duplicate Profiles</h3>
                </div>
                <p class="tool-description">
                    Find profile pairs that share at least 2 of: First Name, Last Name, or Date of Birth.
                    Note: Twins may appear but should not be merged.
                </p>
                <div class="tool-action">
                    <app-btn @click="getDuplicateReport" :loading="isGettingDuplicateReport">
                        <v-icon class="mr-2" size="18">mdi-magnify</v-icon>
                        Get Report
                    </app-btn>
                </div>
            </div>

            <!-- Merge Profiles Tool -->
            <div class="tool-card" v-if="!$store.getters.isEnterpriseAdminOrg">
                <div class="tool-card-header">
                    <div class="tool-icon tool-icon--orange">
                        <v-icon color="white">mdi-merge</v-icon>
                    </div>
                    <h3 class="tool-title">Merge Profiles</h3>
                </div>
                <p class="tool-description">
                    Combine two profiles into one. The "Profile to Keep" will retain all data from both profiles.
                </p>
                <app-form
                    class="tool-form"
                    submit-button-title="Merge Profiles"
                    :saving="isMergingProfiles"
                    @submit="startMerge"
                >
                    <app-text-field
                        label="Profile ID to Keep"
                        v-model="mergeProfilesData.profileToKeepId"
                        rules="required|positive"
                    />
                    <app-text-field
                        label="Profile ID to Lose"
                        v-model="mergeProfilesData.profileToLoseId"
                        rules="required|positive"
                        class="mt-3"
                    />
                </app-form>
            </div>

            <!-- Transfer Profile (Enterprise) -->
            <div class="tool-card" v-if="$store.getters.isEnterpriseAdminOrg && isAdminOrSuper">
                <div class="tool-card-header">
                    <div class="tool-icon tool-icon--purple">
                        <v-icon color="white">mdi-account-arrow-right</v-icon>
                    </div>
                    <h3 class="tool-title">Transfer Profile</h3>
                </div>
                <p class="tool-description">
                    Move a profile to a different organization within your enterprise.
                </p>
                <app-form
                    class="tool-form"
                    submit-button-title="Transfer Profile"
                    :saving="isTransferringProfiles"
                    @submit="startTransferProfile"
                >
                    <app-text-field
                        label="Profile ID"
                        v-model="transferProfileData.profileId"
                        type="number"
                        rules="required|positive"
                    />
                    <AppSelect
                        label="New Organization"
                        v-model="transferProfileData.organizationId"
                        :items="organizationList"
                        item-text="label"
                        item-value="id"
                        class="mt-3"
                    />
                </app-form>
            </div>

            <!-- Transfer Profile (Super Admin) -->
            <div class="tool-card" v-if="account?.isSuperAdmin && !$store.getters.isEnterpriseAdminOrg">
                <div class="tool-card-header">
                    <div class="tool-icon tool-icon--purple">
                        <v-icon color="white">mdi-account-arrow-right</v-icon>
                    </div>
                    <h3 class="tool-title">Transfer Profile</h3>
                    <span class="tool-badge">Super Admin</span>
                </div>
                <p class="tool-description">
                    Move a profile to any organization by ID.
                </p>
                <app-form
                    class="tool-form"
                    submit-button-title="Transfer Profile"
                    :saving="isTransferringProfiles"
                    @submit="startTransferProfile"
                >
                    <app-text-field
                        label="Profile ID"
                        v-model="transferProfileData.profileId"
                        type="number"
                        rules="required|positive"
                    />
                    <app-text-field
                        label="New Organization ID"
                        v-model="transferProfileData.organizationId"
                        type="number"
                        rules="required|positive"
                        class="mt-3"
                    />
                </app-form>
            </div>

            <!-- Transfer Test (Super Admin) -->
            <div class="tool-card" v-if="account?.isSuperAdmin">
                <div class="tool-card-header">
                    <div class="tool-icon tool-icon--green">
                        <v-icon color="white">mdi-file-send</v-icon>
                    </div>
                    <h3 class="tool-title">Transfer Test</h3>
                    <span class="tool-badge">Super Admin</span>
                </div>
                <p class="tool-description">
                    Move a test from one profile to another.
                </p>
                <app-form
                    class="tool-form"
                    submit-button-title="Transfer Test"
                    :saving="isTransferringTest"
                    @submit="onTransferTest"
                >
                    <app-text-field
                        label="Test ID"
                        v-model="transferTestData.profileTestId"
                        rules="required|positive"
                    />
                    <app-text-field
                        label="Current Profile ID"
                        v-model="transferTestData.currentProfileId"
                        rules="required|positive"
                        class="mt-3"
                    />
                    <app-text-field
                        label="New Profile ID"
                        v-model="transferTestData.newProfileId"
                        rules="required|positive"
                        class="mt-3"
                    />
                </app-form>
            </div>

            <!-- Change Tests to Baseline (Super Admin) -->
            <div class="tool-card" v-if="account?.isSuperAdmin">
                <div class="tool-card-header">
                    <div class="tool-icon tool-icon--teal">
                        <v-icon color="white">mdi-swap-vertical</v-icon>
                    </div>
                    <h3 class="tool-title">Change to Baseline</h3>
                    <span class="tool-badge">Super Admin</span>
                </div>
                <p class="tool-description">
                    Convert tests taken on a code from non-baseline to baseline.
                </p>
                <app-form
                    class="tool-form"
                    submit-button-title="Change to Baseline"
                    :saving="isChangingTestsToBaseline"
                    @submit="onChangeTestsToBaseline"
                >
                    <app-text-field
                        label="Code"
                        v-model="changeTestsToBaselineData.code"
                        rules="required"
                    />
                    <app-text-field
                        label="Organization ID"
                        v-model="changeTestsToBaselineData.organizationId"
                        type="number"
                        rules="required|positive"
                        class="mt-3"
                    />
                </app-form>
            </div>

            <!-- Transfer Group of Profiles (Super Admin) -->
            <div class="tool-card tool-card--wide tool-card--warning" v-if="account?.isSuperAdmin">
                <div class="tool-card-header">
                    <div class="tool-icon tool-icon--red">
                        <v-icon color="white">mdi-account-group-outline</v-icon>
                    </div>
                    <h3 class="tool-title">Transfer Group of Profiles</h3>
                    <span class="tool-badge tool-badge--red">Super Admin</span>
                </div>
                <div class="tool-warning">
                    <v-icon color="error" size="18" class="mr-2">mdi-alert</v-icon>
                    <span>All profiles will be <strong>deleted</strong> from the current organization after transfer.</span>
                </div>
                <p class="tool-description">
                    Transfer all profiles in a group to another organization. Enter 0 for "New Group ID" to create a duplicate group.
                </p>
                <app-form
                    class="tool-form"
                    submit-button-title="Transfer Group"
                    :saving="isTransferringGroupOfProfiles"
                    @submit="onTransferGroupOfProfiles"
                >
                    <div class="tool-form-grid">
                        <app-text-field
                            label="Current Org ID"
                            v-model="transferGroupOfProfiles.fromOrgId"
                            rules="required|positive"
                        />
                        <app-text-field
                            label="New Org ID"
                            v-model="transferGroupOfProfiles.toOrgId"
                            rules="required|positive"
                        />
                        <app-text-field
                            label="Current Group ID"
                            v-model="transferGroupOfProfiles.fromGroupIds"
                            rules="required"
                        />
                        <app-text-field
                            label="New Group ID"
                            v-model="transferGroupOfProfiles.toGroupId"
                            rules="required|positive"
                        />
                    </div>
                </app-form>
            </div>
            <!-- Queue Bulk PDF Upload (Super Admin) -->
            <div class="tool-card" v-if="account?.isSuperAdmin">
                <div class="tool-card-header">
                    <div class="tool-icon tool-icon--teal">
                        <v-icon color="white">mdi-file-pdf-box</v-icon>
                    </div>
                    <h3 class="tool-title">Queue Bulk PDF Upload</h3>
                    <span class="tool-badge">Super Admin</span>
                </div>
                <p class="tool-description">
                    Queue a bulk PDF upload job for an organization.
                </p>
                <app-form
                    class="tool-form"
                    submit-button-title="Queue Bulk PDF Upload"
                    :saving="isQueueingBulkPdfUpload"
                    @submit="onQueueBulkPdfUpload"
                >
                    <app-text-field
                        label="Organization ID"
                        v-model="bulkPdfUploadData.organizationId"
                        type="number"
                        rules="required|positive"
                    />
                    <AppSelect
                        label="EHR Partner"
                        v-model="bulkPdfUploadData.ehrPartnerId"
                        :items="ehrPartnerOptions"
                        item-text="label"
                        item-value="id"
                        class="mt-3"
                    />
                    <app-text-field
                        label="Start Date"
                        v-model="bulkPdfUploadData.startDate"
                        type="date"
                        class="mt-3"
                    />
                    <app-text-field
                        label="End Date"
                        v-model="bulkPdfUploadData.endDate"
                        type="date"
                        class="mt-3"
                    />
                    <AppSelect
                        label="Org Time Zone"
                        v-model="bulkPdfUploadData.orgTimeZone"
                        :items="timeZoneOptions"
                        item-text="text"
                        item-value="value"
                        rules="required"
                        class="mt-3"
                    />
                </app-form>
            </div>
        </div>

        <!-- Duplicate Profiles Results Dialog -->
        <Teleport to="body">
            <div v-if="duplicateReportDialogOpen" class="dialog-overlay" @click.self="duplicateReportDialogOpen = false">
                <v-card class="dialog-card" style="width: 700px; max-width: 90vw;">
                    <v-card-title style="display: flex; align-items: center; border-bottom: 1px solid #e0e0e0;">
                        <h2 style="font-weight: bold; flex: 1; font-size: 1.25rem;">Possible Duplicate Profiles</h2>
                        <v-icon @click="duplicateReportDialogOpen = false" color="grey" style="cursor: pointer;">mdi-close</v-icon>
                    </v-card-title>
                    <v-card-text style="padding: 0; max-height: 60vh; overflow-y: auto;">
                        <div v-if="possibleDuplicateProfiles && possibleDuplicateProfiles.length === 0" style="padding: 24px; text-align: center; color: #757575;">
                            <v-icon size="48" color="grey" class="mb-2">mdi-check-circle-outline</v-icon>
                            <div>No possible duplicates found</div>
                        </div>
                        <div v-else-if="possibleDuplicateProfiles && possibleDuplicateProfiles.length > 0">
                            <div v-for="(pair, i) in possibleDuplicateProfiles" :key="i" class="duplicate-pair" :class="{ 'duplicate-pair--alt': i % 2 === 1 }">
                                <div class="duplicate-pair-profiles">
                                    <div class="duplicate-profile">
                                        <div class="duplicate-profile-id">#{{ pair.profile1.id }}</div>
                                        <div v-if="!viewingAsSuper" class="duplicate-profile-name">{{ pair.profile1.firstName }} {{ pair.profile1.lastName }}</div>
                                        <div v-if="!viewingAsSuper" class="duplicate-profile-dob">{{ pair.profile1.birthDate }}</div>
                                    </div>
                                    <div class="duplicate-pair-vs">
                                        <v-icon size="20" color="grey">mdi-swap-horizontal</v-icon>
                                    </div>
                                    <div class="duplicate-profile">
                                        <div class="duplicate-profile-id">#{{ pair.profile2.id }}</div>
                                        <div v-if="!viewingAsSuper" class="duplicate-profile-name">{{ pair.profile2.firstName }} {{ pair.profile2.lastName }}</div>
                                        <div v-if="!viewingAsSuper" class="duplicate-profile-dob">{{ pair.profile2.birthDate }}</div>
                                    </div>
                                </div>
                                <div class="duplicate-pair-action">
                                    <app-btn small @click="startMergeFromReport(pair.profile1, pair.profile2)">
                                        Review
                                    </app-btn>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-actions v-if="possibleDuplicateProfiles && possibleDuplicateProfiles.length > 0" style="padding: 12px 16px; border-top: 1px solid #e0e0e0; justify-content: space-between;">
                        <div style="color: #757575; font-size: 0.875rem;">
                            {{ possibleDuplicateProfiles.length }} possible duplicate pair{{ possibleDuplicateProfiles.length === 1 ? '' : 's' }} found
                        </div>
                        <app-btn outlined @click="duplicateReportDialogOpen = false">Close</app-btn>
                    </v-card-actions>
                </v-card>
            </div>
        </Teleport>

        <!-- Merge Confirm Dialog -->
        <app-confirm-dialog
            :open="!!(mergeProfilesData.profileToKeepName && mergeProfilesData.profileToLoseName)"
            title="Confirm Merge"
            @close="cancelMerge"
            @confirm="confirmMerge"
            :loading="isMergingProfiles"
            :disable-confirm="mergeValidationFailed"
        >
            <div>
                <p>Are you sure you want to merge these profiles? Ensure no tests are in progress.</p>
                <div class="merge-preview">
                    <div class="merge-preview-item merge-preview-item--keep">
                        <span class="merge-preview-label">Keep:</span>
                        <span class="merge-preview-value">{{ mergeProfilesData.profileToKeepName }}</span>
                        <span class="merge-preview-value">ID: {{ mergeProfilesData.profileToKeepId }}</span>
                    </div>
                    <div class="merge-preview-item merge-preview-item--lose">
                        <span class="merge-preview-label">Lose:</span>
                        <span class="merge-preview-value">{{ mergeProfilesData.profileToLoseName }}</span>
                        <span class="merge-preview-value">ID: {{ mergeProfilesData.profileToLoseId }}</span>
                    </div>
                </div>
                <div style="text-align: center; margin-top: 12px; display: flex; justify-content: center; gap: 12px; flex-wrap: wrap;">
                    <app-btn small outlined @click="swapMerge">
                        <v-icon size="18" class="mr-1">mdi-swap-vertical</v-icon>
                        Swap
                    </app-btn>
                    <app-btn small outlined @click="startMarkAsNonDuplicate">
                        Not a Duplicate
                    </app-btn>
                </div>
            </div>
        </app-confirm-dialog>

        <!-- Mark as Not a Duplicate Confirm Dialog -->
        <app-confirm-dialog
            :open="markAsNonDuplicateDialogOpen"
            title="Mark as Not a Duplicate?"
            submit-button-title="Not a Duplicate"
            @close="cancelMarkAsNonDuplicate"
            @confirm="confirmMarkAsNonDuplicate"
            :loading="isMarkingAsNonDuplicate"
        >
            <div>
                <p>Are you sure these profiles are not duplicates? They won't appear in the duplicate report again.</p>
                <div class="merge-preview">
                    <div class="merge-preview-item">
                        <span class="merge-preview-label">Profile 1:</span>
                        <span class="merge-preview-value">{{ mergeProfilesData.profileToKeepName }}</span>
                        <span class="merge-preview-value">ID: {{ mergeProfilesData.profileToKeepId }}</span>
                    </div>
                    <div class="merge-preview-item">
                        <span class="merge-preview-label">Profile 2:</span>
                        <span class="merge-preview-value">{{ mergeProfilesData.profileToLoseName }}</span>
                        <span class="merge-preview-value">ID: {{ mergeProfilesData.profileToLoseId }}</span>
                    </div>
                </div>
            </div>
        </app-confirm-dialog>

        <!-- Transfer Profile Confirm Dialog -->
        <app-confirm-dialog
            :open="!!transferProfileData.profileName"
            title="Confirm Transfer"
            @close="cancelTransferProfile"
            @confirm="confirmTransferProfile"
            :loading="isTransferringProfiles"
        >
            <p>Are you sure you want to transfer this profile?</p>
            <div class="merge-preview">
                <div class="merge-preview-item">
                    <span class="merge-preview-label">Profile:</span>
                    <span class="merge-preview-value">{{ transferProfileData.profileName }}</span>
                </div>
            </div>
        </app-confirm-dialog>

    </div>
</template>

<script>
import {
    startMergeProfiles, confirmMergeProfiles, startTransferProfile, confirmTransferProfile,
    transferTest, transferGroupOfProfiles, getDuplicateReport, markAsNonDuplicate,
    changeTestsToBaseline, queueBulkPdfUpload
} from "@/services/tools.service";
import {SET_SNACKBAR} from "@/store/mutation-types";
import {isAdminOrgAccessType} from "@/js/shared/helper";
import AppTextArea from "@/components/forms/AppTextArea.vue";
import PageHeader from "@/components/PageHeader.vue";

export default {
    name: "Tools",
    components: {AppTextArea, PageHeader},
    data() {
        return {
            possibleDuplicateProfiles: null,
            isGettingDuplicateReport: false,
            duplicateReportDialogOpen: false,
            
            mergeProfilesData: {
                profileToKeepId: null,
                profileToKeepName: null,
                profileToLoseId: null,
                profileToLoseName: null,
            },
            isMergingProfiles: false,
            // True when StartMergeProfiles returned a validation error — we still
            // show the dialog (so "Not a Duplicate" stays reachable) but disable
            // Confirm since the server already rejected the merge.
            mergeValidationFailed: false,

            markAsNonDuplicateDialogOpen: false,
            isMarkingAsNonDuplicate: false,

            bulkMergeString:
`ProfileToKeepId,ProfileToLoseId
`,
            isBulkMergingProfiles: false,

            transferProfileData: {
                profileId: null,
                organizationId: null
            },
            isTransferringProfiles: false,
            
            transferTestData: {
                profileTestId: null,
                currentProfileId: null,
                newProfileId: null,
            },
            isTransferringTest: false,

            transferGroupOfProfiles: {
                fromOrgId: null,
                toOrgId: null,
                fromGroupIds: null,
                toGroupId: null,
            },
            isTransferringGroupOfProfiles: false,

            changeTestsToBaselineData: {
                code: null,
                organizationId: null,
            },
            isChangingTestsToBaseline: false,

            bulkPdfUploadData: {
                organizationId: null,
                ehrPartnerId: null,
                startDate: null,
                endDate: null,
                orgTimeZone: 'America/Chicago',
            },
            isQueueingBulkPdfUpload: false
        }
    },
    props: {
        account: {}
    },
    computed: {
        viewingAsSuper() {
            return this.$store.getters.viewingAsSuper;
        },
        organizationList() {
            return this.$store.getters.availableOrganizations
                    .filter(x => !x.isDisabled && !x.isEnterpriseAdminOrg)
                    .map(x => ({label: x.name, id: x.id}));
        },
        isAdminOrSuper(){
            return this.$store.getters.isSuperAdmin
                    || this.isAdminOrgAccessType(this.$store.getters.selectedOrganizationAccessTypeId)
        },
        ehrPartnerOptions() {
            return [
                { label: 'ATS', id: 1 },
                { label: 'Healthy Roster', id: 4 },
                { label: 'SportsWareOnLine', id: 7 },
                { label: 'Teamworks', id: 9 }
            ]
        },
        timeZoneOptions() {
            return [
                // United States
                { text: 'America/New_York (Eastern)', value: 'America/New_York' },
                { text: 'America/Chicago (Central)', value: 'America/Chicago' },
                { text: 'America/Denver (Mountain)', value: 'America/Denver' },
                { text: 'America/Phoenix (Arizona - no DST)', value: 'America/Phoenix' },
                { text: 'America/Los_Angeles (Pacific)', value: 'America/Los_Angeles' },
                { text: 'America/Juneau (Alaska)', value: 'America/Juneau' },
                { text: 'Pacific/Honolulu (Hawaii)', value: 'Pacific/Honolulu' },
                // Canada
                { text: 'America/St_Johns (Newfoundland)', value: 'America/St_Johns' },
                { text: 'America/Halifax (Atlantic)', value: 'America/Halifax' },
                { text: 'America/Toronto (Eastern)', value: 'America/Toronto' },
                { text: 'America/Winnipeg (Central)', value: 'America/Winnipeg' },
                { text: 'America/Edmonton (Mountain)', value: 'America/Edmonton' },
                { text: 'America/Regina (Saskatchewan - no DST)', value: 'America/Regina' },
                { text: 'America/Vancouver (Pacific)', value: 'America/Vancouver' },
                // Europe
                { text: 'Europe/London (GMT/BST)', value: 'Europe/London' },
                { text: 'Europe/Paris (CET/CEST)', value: 'Europe/Paris' },
                { text: 'Europe/Istanbul (EET/EEST)', value: 'Europe/Istanbul' },
                { text: 'Europe/Lisbon (WET/WEST)', value: 'Europe/Lisbon' },
                { text: 'Europe/Moscow (MSK)', value: 'Europe/Moscow' },
                // Americas
                { text: 'America/Sao_Paulo (BRT)', value: 'America/Sao_Paulo' },
                { text: 'America/Argentina/Buenos_Aires (ART)', value: 'America/Argentina/Buenos_Aires' },
                { text: 'America/Santiago (CLT)', value: 'America/Santiago' },
                { text: 'America/Bogota (COT)', value: 'America/Bogota' },
                { text: 'America/Lima (PET)', value: 'America/Lima' },
                // Asia
                { text: 'Asia/Dubai (GST)', value: 'Asia/Dubai' },
                { text: 'Asia/Tehran (IRST)', value: 'Asia/Tehran' },
                { text: 'Asia/Karachi (PKT)', value: 'Asia/Karachi' },
                { text: 'Asia/Calcutta (IST)', value: 'Asia/Calcutta' },
                { text: 'Asia/Dhaka (BDT)', value: 'Asia/Dhaka' },
                { text: 'Asia/Bangkok (ICT)', value: 'Asia/Bangkok' },
                { text: 'Asia/Jakarta (WIT)', value: 'Asia/Jakarta' },
                { text: 'Asia/Singapore (SGT)', value: 'Asia/Singapore' },
                { text: 'Asia/Manila (PHT)', value: 'Asia/Manila' },
                { text: 'Asia/Hong_Kong (HKT)', value: 'Asia/Hong_Kong' },
                { text: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
                { text: 'Asia/Seoul (KST)', value: 'Asia/Seoul' },
                // Africa
                { text: 'Africa/Lagos (WAT)', value: 'Africa/Lagos' },
                { text: 'Africa/Harare (CAT)', value: 'Africa/Harare' },
                { text: 'Africa/Addis_Ababa (EAT)', value: 'Africa/Addis_Ababa' },
                // Pacific
                { text: 'Pacific/Auckland (NZST)', value: 'Pacific/Auckland' },
            ]
        }
    },
    methods: {
        isAdminOrgAccessType,
        async getDuplicateReport(){
            this.isGettingDuplicateReport = true;

            let response = await getDuplicateReport();

            if (!response.isSuccess) {
                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            }
            else{
                this.possibleDuplicateProfiles = response.profiles;
                this.duplicateReportDialogOpen = true;
            }

            this.isGettingDuplicateReport = false
        },
        async startMerge() {
            this.isMergingProfiles = true
            this.mergeValidationFailed = false;
            let request = {...this.mergeProfilesData};

            let response = await startMergeProfiles(request);

            if (!response.isSuccess) {
                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
                this.mergeValidationFailed = true;
            }
            else{
                this.mergeProfilesData.profileToLoseName = response.profileToLoseName;
                this.mergeProfilesData.profileToKeepName = response.profileToKeepName;
            }

            this.isMergingProfiles = false
        },
        async startMergeFromReport(profile1, profile2) {
            if (profile1.id < profile2.id) {
                this.mergeProfilesData = {
                    profileToKeepId: profile1.id,
                    profileToKeepName: profile1.firstName + ' ' + profile1.lastName,
                    profileToLoseId: profile2.id,
                    profileToLoseName: profile2.firstName + ' ' + profile2.lastName,
                };
            }else{
                this.mergeProfilesData = {
                    profileToKeepId: profile2.id,
                    profileToKeepName: profile2.firstName + ' ' + profile2.lastName,
                    profileToLoseId: profile1.id,
                    profileToLoseName: profile1.firstName + ' ' + profile1.lastName,
                };
            }

            await this.startMerge();
        },
        swapMerge() {
            this.mergeProfilesData = {
                profileToKeepId: this.mergeProfilesData.profileToLoseId,
                profileToKeepName: this.mergeProfilesData.profileToLoseName,
                profileToLoseId: this.mergeProfilesData.profileToKeepId,
                profileToLoseName: this.mergeProfilesData.profileToKeepName,
            };
        },
        async confirmMerge() {
            let request = {...this.mergeProfilesData};

            let response = await confirmMergeProfiles(request);

            if (!response.isSuccess) {

                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            } else {
                this.$store.commit(SET_SNACKBAR, {
                    text: `Successfully merged profiles.`,
                    color: 'success',
                    open: true
                });

                this.mergeProfilesData.profileToKeepId = null
                this.mergeProfilesData.profileToKeepName = null

                this.mergeProfilesData.profileToLoseId = null
                this.mergeProfilesData.profileToLoseName = null
            }

            if (this.possibleDuplicateProfiles)
                this.getDuplicateReport();

            this.isMergingProfiles = false
        },
        async cancelMerge(){
            this.mergeProfilesData.profileToKeepId = null
            this.mergeProfilesData.profileToKeepName = null

            this.mergeProfilesData.profileToLoseId = null
            this.mergeProfilesData.profileToLoseName = null

            this.isMergingProfiles = false;
            this.mergeValidationFailed = false;
        },
        startMarkAsNonDuplicate() {
            this.markAsNonDuplicateDialogOpen = true;
        },
        cancelMarkAsNonDuplicate() {
            this.markAsNonDuplicateDialogOpen = false;
            this.isMarkingAsNonDuplicate = false;
        },
        async confirmMarkAsNonDuplicate() {
            this.isMarkingAsNonDuplicate = true;

            const response = await markAsNonDuplicate({
                profileId1: this.mergeProfilesData.profileToKeepId,
                profileId2: this.mergeProfilesData.profileToLoseId,
            });

            if (!response || !response.isSuccess) {
                this.$store.commit(SET_SNACKBAR, {
                    text: response?.errors?.[0]?.message || 'Failed to mark as not a duplicate.',
                    color: 'error',
                    open: true
                });
                this.isMarkingAsNonDuplicate = false;
                return;
            }

            this.$store.commit(SET_SNACKBAR, {
                text: 'Marked as not a duplicate.',
                color: 'success',
                open: true
            });

            this.markAsNonDuplicateDialogOpen = false;

            // Close the parent merge dialog and clear the active pair so the
            // user is returned to the duplicate report list.
            this.mergeProfilesData.profileToKeepId = null;
            this.mergeProfilesData.profileToKeepName = null;
            this.mergeProfilesData.profileToLoseId = null;
            this.mergeProfilesData.profileToLoseName = null;

            if (this.possibleDuplicateProfiles)
                this.getDuplicateReport();

            this.isMarkingAsNonDuplicate = false;
        },
        async mergeProfileBulk(){
            this.isBulkMergingProfiles = true;
            
            let errorMessage = "";
            let mergeRequests = [];
            let mergeLines = this.bulkMergeString.split("\n");
            for (let i = 0; i < mergeLines.length; i++)
            {
                if (!mergeLines[i] || mergeLines[i].startsWith("ProfileToKeepId,ProfileToLoseId"))
                    continue;
                
                let mergeRequest = {};
                try{
                    mergeRequest.profileToKeepId = Number(mergeLines[i].split(",")[0]);
                    mergeRequest.profileToLoseId = Number(mergeLines[i].split(",")[1]);
                    if (!mergeRequest.profileToKeepId){
                        errorMessage += "Invalid profile to keep on row " + (i+1)
                    }
                    if (!mergeRequest.profileToLoseId){
                        errorMessage += "Invalid profile to lose on row " + (i+1)
                    }
                    
                    mergeRequests.push(mergeRequest);
                }
                catch(error){
                    errorMessage += "Invalid format on row " + (i+1)
                }
            }
            
            if (errorMessage)
            {
                this.$store.commit(SET_SNACKBAR, {
                    text: errorMessage,
                    color: 'error',
                    open: true
                });
            }
            else
            {
                for (let i = 0; i < mergeRequests.length; i++)
                {
                    try
                    {
                        let request = {...mergeRequests[i]};

                        let response = await confirmMergeProfiles(request);
                        if (!response.isSuccess) {

                            this.$store.commit(SET_SNACKBAR, {
                                text: response.errors[0].message,
                                color: 'error',
                                open: true
                            });
                            return;
                        }
                    }
                    catch(error)
                    {
                        this.$store.commit(SET_SNACKBAR, {
                            text: "Failure to merge on row " + (i+1),
                            color: 'error',
                            open: true
                        });
                        this.isBulkMergingProfiles = false;
                        return;
                    }
                }
                
                this.$store.commit(SET_SNACKBAR, {
                    text: `Successfully merged profiles.`,
                    color: 'success',
                    open: true
                });
            }

            this.isBulkMergingProfiles = false;
        },
        async startTransferProfile() {
            this.isTransferringProfiles = true
            let request = {...this.transferProfileData};

            let response = await startTransferProfile(request);

            if (!response.isSuccess) {
                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            }
            else{
                this.transferProfileData.profileName = response.profileName;
                this.transferProfileData.organizationName = response.organizationName;
            }

            this.isTransferringProfiles = false
        },
        async confirmTransferProfile() {
            let request = {...this.transferProfileData};

            let response = await confirmTransferProfile(request);

            if (!response.isSuccess) {

                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            } else {
                this.$store.commit(SET_SNACKBAR, {
                    text: `Successfully transferred profiles.`,
                    color: 'success',
                    open: true
                });

                this.transferProfileData.profileId = null
                this.transferProfileData.organizationId = null
                this.transferProfileData.profileName = null
            }

            this.isTransferringProfiles = false
        },
        async cancelTransferProfile(){
            this.transferProfileData.profileId = null
            this.transferProfileData.organizationId = null
            this.transferProfileData.profileName = null

            this.isTransferringProfiles = false
        },
        async onTransferTest() {

            this.isTransferringTest = true
            let request = {...this.transferTestData};
            let response = await transferTest(request);

            if (!response.isSuccess) {

                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            } else {
                this.$store.commit(SET_SNACKBAR, {
                    text: `Successfully transferred test.`,
                    color: 'success',
                    open: true
                });

                this.transferTestData.newProfileId = null
                this.transferTestData.currentProfileId = null
                this.transferTestData.profileTestId = null
            }

            this.isTransferringTest = false
        },
        async onTransferGroupOfProfiles() {
            
            let confirmation = confirm("Are you sure? All profiles in Group #" + this.transferGroupOfProfiles.fromGroupIds + 
                " will be transferred to Org #" + this.transferGroupOfProfiles.toOrgId + 
                ", and will be deleted from Org #" + this.transferGroupOfProfiles.fromOrgId);
            
            if (!confirmation)
                return;

            this.isTransferringGroupOfProfiles = true
            let request = {...this.transferGroupOfProfiles};
            let response = await transferGroupOfProfiles(request);

            if (!response.isSuccess) {

                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            } else {
                this.$store.commit(SET_SNACKBAR, {
                    text: `Successfully transferred group of profiles.`,
                    color: 'success',
                    open: true
                });

                this.transferGroupOfProfiles.fromOrgId = null
                this.transferGroupOfProfiles.toOrgId = null
                this.transferGroupOfProfiles.fromGroupIds = null
                this.transferGroupOfProfiles.toGroupId = null
            }

            this.isTransferringGroupOfProfiles = false
        },
        async onChangeTestsToBaseline() {
            this.isChangingTestsToBaseline = true
            let request = {...this.changeTestsToBaselineData};
            let response = await changeTestsToBaseline(request);

            if (!response.isSuccess) {
                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            } else {
                this.$store.commit(SET_SNACKBAR, {
                    text: `Successfully changed tests to baseline.`,
                    color: 'success',
                    open: true
                });

                this.changeTestsToBaselineData.code = null
                this.changeTestsToBaselineData.organizationId = null
            }

            this.isChangingTestsToBaseline = false
        },
        async onQueueBulkPdfUpload() {
            this.isQueueingBulkPdfUpload = true
            let request = {...this.bulkPdfUploadData};
            let response = await queueBulkPdfUpload(request);

            if (!response.isSuccess) {
                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors[0].message,
                    color: 'error',
                    open: true
                });
            } else {
                this.$store.commit(SET_SNACKBAR, {
                    text: `Successfully queued bulk PDF upload.`,
                    color: 'success',
                    open: true
                });

                this.bulkPdfUploadData.organizationId = null
                this.bulkPdfUploadData.ehrPartnerId = null
                this.bulkPdfUploadData.startDate = null
                this.bulkPdfUploadData.endDate = null
                this.bulkPdfUploadData.orgTimeZone = 'Central Standard Time'
            }

            this.isQueueingBulkPdfUpload = false
        }
        
    }
}
</script>

<style scoped>
.tools-page {
    max-width: 1200px;
}

.tools-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 24px;
    margin-top: 24px;
}

.tool-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #E5E7EB;
    transition: box-shadow 0.2s ease;
}

.tool-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.tool-card--wide {
    grid-column: span 2;
}

.tool-card--warning {
    border-color: #FCA5A5;
    background: linear-gradient(to bottom, #FEF2F2, white);
}

.tool-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.tool-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #33D3FF;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.tool-icon--orange { background: #F59E0B; }
.tool-icon--purple { background: #8B5CF6; }
.tool-icon--green { background: #10B981; }
.tool-icon--teal { background: #14B8A6; }
.tool-icon--red { background: #EF4444; }

.tool-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1F2937;
    margin: 0;
    flex: 1;
}

.tool-badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    background: #E0F2FE;
    color: #0369A1;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.tool-badge--red {
    background: #FEE2E2;
    color: #DC2626;
}

.tool-description {
    color: #6B7280;
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 16px;
}

.tool-warning {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    background: #FEE2E2;
    border-radius: 8px;
    margin-bottom: 12px;
    font-size: 0.85rem;
    color: #991B1B;
}

.tool-form {
    margin-top: 8px;
}

.tool-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.tool-action {
    margin-top: 8px;
}

/* Merge Preview Styles */
.merge-preview {
    background: #F9FAFB;
    border-radius: 8px;
    padding: 12px;
    margin-top: 12px;
}

.merge-preview-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
}

.merge-preview-item:not(:last-child) {
    border-bottom: 1px solid #E5E7EB;
}

.merge-preview-item--keep .merge-preview-label {
    color: #059669;
}

.merge-preview-item--lose .merge-preview-label {
    color: #DC2626;
}

.merge-preview-label {
    font-weight: 600;
    width: 60px;
    font-size: 0.85rem;
}

.merge-preview-value {
    flex: 1;
    font-weight: 500;
}

/* Dialog Styles */
.dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 10vh;
    z-index: 2000;
}

.dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
}

/* Duplicate Pairs Styles */
.duplicate-pair {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
}

.duplicate-pair--alt {
    background-color: #fafafa;
}

.duplicate-pair:last-child {
    border-bottom: none;
}

.duplicate-pair-profiles {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 12px;
}

.duplicate-profile {
    flex: 1;
    min-width: 0;
}

.duplicate-profile-id {
    font-weight: 600;
    color: #33D3FF;
    font-size: 0.875rem;
}

.duplicate-profile-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.duplicate-profile-dob {
    font-size: 0.8rem;
    color: #757575;
}

.duplicate-pair-vs {
    padding: 0 8px;
}

.duplicate-pair-action {
    margin-left: 12px;
}

@media (max-width: 768px) {
    .tools-grid {
        grid-template-columns: 1fr;
    }

    .tool-card--wide {
        grid-column: span 1;
    }

    .tool-form-grid {
        grid-template-columns: 1fr;
    }
}
</style>